/**
 * Hillshade for the strategic chart — the ground given a shape.
 *
 * The campaign map was flat, and it was flat while the generator knew the
 * height of every cell on the board. `RIDGE_FIELD` in `data/realm.ts` is
 * precomputed over the raster and decides where every mountain, hill and dry
 * plain goes; `elevationAt` reads it; the biome chooser turns it into
 * `'mountain'` and `'hill'`. Nothing ever drew it. The chart painted a flat
 * colour per province and scattered a few triangle glyphs on top, so a range
 * and a heath differed by a hue and a handful of marks.
 *
 * This is the same field, lit. It is the oldest trick in relief cartography and
 * still the one that does the most: a light from the north-west, the slope
 * measured against it, and the answer laid over the colours. Nothing about the
 * board changes — no province moves, no biome is reassigned — and the map stops
 * being a diagram.
 *
 * ## Why a raster and not SVG
 *
 * Everything else on this chart is vector, for good reasons written up in
 * `cartography.ts`. Shading is the exception: it is a value per cell over a
 * 367×257 grid, which is a bitmap by nature, and expressing it as ninety-four
 * thousand little polygons would be slower to make, slower to draw and no
 * sharper. So it is baked once into a canvas and laid in as one `<image>`.
 *
 * Baked once with fine ridge detail and reused at every zoom. The image URLs
 * reference cached blobs, so a campaign redraw does not copy megabytes of
 * encoded pixels into its SVG markup.
 */

import { RASTER, WORLD, hash2, wasteness } from '../data/realm.ts';
import { provinceDef } from '../data/provinces.ts';
import type { ProvinceTerrain } from '../data/realm.ts';
import { warp } from '../data/cartography.ts';

// ── One coordinate system for the whole chart ───────────────────────────────
//
// **The rasters and the lines were drawn in two different places, and nobody
// had measured the gap.**
//
// Every *line* on this map — the coastline, the province rings, the borders,
// the roads — is displaced at the very end by `cartography.ts`'s plane warp,
// which is what stops a border reading as the smoothed staircase it is
// underneath. Every *raster* — this hillshade, the fabric, the sea floor, the
// strand — is painted straight onto the sheet at the position the raster cell
// has. So the drawn coast and the painted beach were never in the same place,
// and `npm run coastline` puts the number on it: **median 2.0 map units apart,
// p90 3.4, worst 6.0.**
//
// That was survivable while the only thing following the raster was a shading.
// It stopped being survivable here, because this pass does two things that make
// it visible: it densifies every line before the warp, which moves the *middle*
// of a straight stretch rather than only its ends, and it paints the biome as a
// per-cell colour with a hard edge at the water.
//
// So the bakes go through the warp backwards. Pixel `q` on the sheet reads the
// field at `unwarp(q)`, and the painted ground lands under the drawn line. One
// grid, computed once at the pitch every bake already uses, shared by all four.
//
// **The cost, measured**: 663,000 grid points, three Newton steps apiece,
// warm-started from the point to the left — the field is smooth at five map
// units, so the neighbour's answer starts the next one within a twentieth of a
// unit and three steps take it to a thousandth. About 40 ms and 5.3 MB, once
// per session, against four bakes that already cost 663,000 pixels each.

/** Grid samples to a raster cell — the pitch `RELIEF_SS`, `TEX_SS` and the
 *  shore bake all use, so those three read it without interpolating. */
const PLANE_SS = 2;
let planeDX: Float32Array | null = null;
let planeDY: Float32Array | null = null;

function buildPlane(): void {
  const { cols, rows, cell, originX, originY } = RASTER;
  const w = cols * PLANE_SS;
  const h = rows * PLANE_SS;
  const dx = new Float32Array(w * h);
  const dy = new Float32Array(w * h);
  const step = cell / PLANE_SS;
  for (let r = 0; r < h; r++) {
    // Warm start: the first point of a row inherits the row above, every other
    // point inherits its left neighbour. The inverse is as smooth as the warp.
    let sx = r > 0 ? dx[(r - 1) * w] : 0;
    let sy = r > 0 ? dy[(r - 1) * w] : 0;
    for (let c = 0; c < w; c++) {
      const qx = originX + c * step;
      const qy = originY + r * step;
      let px = qx + sx;
      let py = qy + sy;
      for (let i = 0; i < 3; i++) {
        const wp = warp({ x: px, y: py });
        px = qx - (wp.x - px);
        py = qy - (wp.y - py);
      }
      sx = px - qx;
      sy = py - qy;
      const k = r * w + c;
      dx[k] = sx;
      dy[k] = sy;
    }
  }
  planeDX = dx;
  planeDY = dy;
}

/**
 * Where a point of the *drawn sheet* came from on the raster, in raster-cell
 * coordinates — the coordinates every bake in this file and in `shore.ts`
 * already works in.
 *
 * Bilinear over the grid above. At `PLANE_SS` the grid lattice is the bake's own
 * pixel lattice, so a bake at that pitch is reading exact values and the
 * interpolation is only exercised by the sea, which is baked one-to-one.
 */
export function planeSource(fx: number, fy: number): { x: number; y: number } {
  if (!planeDX || !planeDY) buildPlane();
  const gx = planeDX as unknown as Float32Array;
  const gy = planeDY as unknown as Float32Array;
  const { cols, rows, cell } = RASTER;
  const w = cols * PLANE_SS;
  const h = rows * PLANE_SS;
  const ax = Math.max(0, Math.min(w - 1.001, fx * PLANE_SS));
  const ay = Math.max(0, Math.min(h - 1.001, fy * PLANE_SS));
  const x0 = ax | 0;
  const y0 = ay | 0;
  const tx = ax - x0;
  const ty = ay - y0;
  const k = y0 * w + x0;
  const sx =
    (gx[k] + (gx[k + 1] - gx[k]) * tx) * (1 - ty) +
    (gx[k + w] + (gx[k + w + 1] - gx[k + w]) * tx) * ty;
  const sy =
    (gy[k] + (gy[k + 1] - gy[k]) * tx) * (1 - ty) +
    (gy[k + w] + (gy[k + w + 1] - gy[k + w]) * tx) * ty;
  return { x: fx + sx / cell, y: fy + sy / cell };
}

/**
 * Where the light comes from.
 *
 * North-west, which is both the cartographic convention and — much more
 * importantly here — the same corner everything else in this game is lit from.
 * `drawBuilding` lights its left face, `curtainart.ts` lights the left of a
 * tower, the resource icons put their lit plane top-left. A chart lit from the
 * other side would be the one surface in the game disagreeing with the rest.
 */
export const LIGHT_X = -0.7071;
export const LIGHT_Y = -0.7071;

/**
 * How hard the slope is pushed before it is lit — **at sea level**, and rising
 * steeply with altitude.
 *
 * Relief maps have exaggerated vertical scale for a reason: the field runs
 * 0..1 over a board 1,290 units wide, so its true gradients are tiny and an
 * honest lighting of them is a flat grey sheet. The question is only how much,
 * and the first cut of this got the answer badly wrong in an instructive way.
 *
 * It used one number, 105, for the whole board. Measured, the per-cell gradient
 * on land runs: median 0.022, p90 0.051, p99 0.079. At 105 the *median* slope
 * arrives at the lighting as 2.3 — which is already far past vertical, already
 * saturated. **The entire dynamic range was being spent on flat ground**, so a
 * mountain and a heath came out of the shader within a few percent of each
 * other and the ranges read as undulation. That is the whole reason mountains
 * looked like a sketch: the only thing actually saying "mountain" was the
 * little triangle glyph drawn on top.
 *
 * So the exaggeration is a function of height. Low ground gets `BASE`, which is
 * gentle enough to leave a plain nearly smooth; high ground gets up to
 * `BASE + PEAK`, which is where the contrast now goes. The curve is quadratic
 * so the transition happens in the top third rather than linearly across
 * everything.
 */
const RELIEF_BASE = 2.7;
const RELIEF_PEAK = 15;

/**
 * Where rock gives way to snow, in the height field's own units.
 *
 * Land runs 0.10 to 1.12 with a median of 0.36 and a p90 of 0.73, so this puts
 * the snowline just above the ninetieth percentile: it is a crown on the true
 * high ground rather than a wash over every hill. Hypsometric tint — colouring
 * by altitude — is the oldest and most legible mountain cue there is, and it is
 * the half of "this is high" that shading alone cannot say, because a shaded
 * slope looks the same at any elevation.
 */
const SNOW_FROM = 0.82;
const SNOW_FULL = 1.12;

/**
 * …and where it lies at the poles, which is nearly on the beach.
 *
 * One snowline for a world that runs from a rainforest to an ice shelf is a
 * statement about the paper rather than about the world — the same class of bug
 * `realm.ts` names three times. Measured on this board: **11,846 land cells sit
 * in the wastes (`wasteness > 0.5`) and only 1,293 of them, 10.9%, were above
 * the one snowline of 0.82.** So nine tenths of the Rimewreath was bare rock,
 * and the only thing saying "ice" there was a flat white province fill — which
 * is precisely the "white paint, not cold ground" the chart was accused of.
 *
 * `wasteness` is the generator's own latitude term: the same function that
 * decides a region is a waste in the first place, imported rather than
 * re-derived, because a second copy of `EQUATOR` in the drawing code is a copy
 * that drifts the first time the sheet moves. At the equator the snowline is
 * where it was; at the poles it drops to `SNOW_POLAR` and the ramp above it is
 * `SNOW_BAND` tall, so polar hills are white and the polar shore is not quite.
 */
const SNOW_POLAR = 0.14;
const SNOW_BAND = 0.3;

/**
 * How ragged the snowline is, in the height field's units.
 *
 * A snowline drawn at one number is a contour, and a contour is the one thing
 * on a chart that unmistakably says "computed". Real snow ends in fingers down
 * the gullies and bare patches on the shoulders, and the cheapest honest way to
 * say that is to wobble the threshold itself rather than to stipple the result.
 */
const SNOW_RAGGED = 0.075;

/**
 * Where snow lies, 0..1 — height against a snowline that falls with latitude.
 *
 * Shared by the hillshade, which uses it to keep a peak's highlight off the
 * floor, and by the ground layer, which uses it to make the snow a *colour*.
 * One function, because two copies of a snowline is two snowlines.
 *
 * `x`/`y` are fine-grid coordinates in raster cells; `wy` is the world y of the
 * cell, which is what latitude is measured in.
 */
export function snowAt(h: number, x: number, y: number, wy: number, icy: boolean): number {
  // An ice province is cold ground whatever its latitude says — the biome
  // chooser has already weighed rain, height and latitude together and come out
  // with "ice", and a drawing that argued with it would be drawing a different
  // world from the one the campaign is played on.
  const cold = Math.max(wasteness(wy), icy ? 0.88 : 0);
  const from =
    SNOW_FROM + (SNOW_POLAR - SNOW_FROM) * cold + (vnoise2(x * 0.14, y * 0.14, 311) - 0.5) * 2 * SNOW_RAGGED;
  const full = cold > 0 ? from + SNOW_BAND : SNOW_FULL;
  if (h <= from) return 0;
  return Math.min(1, (h - from) / Math.max(1e-3, full - from));
}

/**
 * Ridge detail, added to the height field **for shading only**.
 *
 * This is the piece the first two attempts were missing, and no amount of
 * exaggeration was going to substitute for it. `elevationAt` is a ridge lookup
 * plus three octaves of fbm at frequency 0.019 — a period of about fifty map
 * units, or twenty-five raster cells. It is smooth *by design*, because what
 * the simulation wants from it is a biome per province, and a field that
 * wobbled every other cell would scatter mountain provinces through a plain.
 *
 * But smooth is exactly what cannot be lit into a mountain. Light a rounded
 * blob from any angle and it stays a rounded blob; ranges read as ridges
 * because they have *edges* — a lit face, a crest, a shadowed face, repeated at
 * a scale of a few cells. There was nothing at that scale in the field at all,
 * which is why the ranges came out as undulation however hard they were pushed.
 *
 * So the bake adds its own high-frequency term before differencing. Three
 * octaves with the first one **ridged** — folded about its midpoint, which is
 * the standard way to turn smooth noise into creases — and the whole thing
 * scaled by altitude squared, so a plain keeps the smooth field it always had
 * and only high ground grows structure.
 *
 * **It is render-only and must stay so.** It never touches `RASTER.height`,
 * nothing outside this file sees it, and no province changes its terrain
 * because of it. That is the licence for inventing detail here at all: it is a
 * statement about how the ground is *drawn*, not about what it is.
 */
const DETAIL_AMP = 0.055;

/**
 * The extra amplitude and octaves given to ground that *is* relief.
 *
 * Two references settled this. A ridgeline massif and a shaded-relief render of
 * a dissected volcano have the same thing in common and it is not peaks: it is
 * **branching erosion**. A range is not a row of summits, it is one mass cut
 * into spurs and gullies by water, and what carries that is many octaves of
 * ridged noise — each fold adding a finer tributary to the one above it.
 *
 * Four octaves in mountain and hill provinces, one everywhere else. The gate is
 * the province's own terrain rather than a height threshold, which is both
 * truer — a high plain is not a mountain — and much cheaper, because the
 * expensive branch then runs on about a third of the land instead of all of it.
 */
const RELIEF_OCTAVES = 3;
const RELIEF_AMP = 0.26;

/**
 * The coarsest and finest erosion features, in cycles per raster cell.
 *
 * These are pinned to the *bake's own resolution* and that is the point. The
 * first attempt ran four octaves at a ratio of 2.05 from 0.19, which puts the
 * fourth at 1.64 cycles per cell — finer than the grid it is sampled on. An
 * octave below the sampling rate is not detail, it is aliasing: it contributes
 * noise, costs a quarter of the budget, and buys nothing an eye can see. It is
 * the same mistake the ground fabric made in the other direction, and the same
 * lesson — a texture has to be authored against the pitch it will be *drawn*
 * at, not against how fine it would be nice to be.
 *
 * A cell is half a map unit at RELIEF_SS 2, so 0.19 is a feature every ten map
 * units and 0.69 is one every three: coarse spurs down to the finest gully the
 * raster can actually hold.
 */
const EROSION_COARSE = 0.19;
const EROSION_RATIO = 1.9;

/**
 * Relief pixels to a raster cell.
 *
 * This layer was baked one-to-one at first, on the argument that the height
 * field is smooth at ten map units and a finer bake would invent detail it does
 * not have. That was true then and stopped being true the moment ridge detail
 * was added: the detail is a pure function of position, so evaluating it at a
 * finer step is honest, and its *edges* are the entire read. At one-to-one the
 * mountains went visibly soft two notches into the zoom, which is exactly where
 * a player looks at ground.
 */
const RELIEF_SS = 3;

let cached = '';

/** Keep a baked raster out of the SVG's markup. These three images live for the
 * session, so their blob URLs are deliberately retained with the existing
 * caches; no encoding, allocation or URL creation happens while navigating. */
function rasterUrl(canvas: HTMLCanvasElement): string {
  const data = canvas.toDataURL('image/png');
  if (typeof URL.createObjectURL !== 'function') return data;
  const encoded = data.slice(data.indexOf(',') + 1);
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type: 'image/png' }));
}

/**
 * The hillshade as a cached image URL, computed on first ask and kept.
 *
 * Returns `''` if a canvas cannot be had — a chart without shading is the chart
 * that shipped yesterday, and a map that throws is not.
 */
export function reliefImage(): string {
  if (cached !== '') return cached;
  const { cols, rows, owner, height, originY, cell } = RASTER;
  const w = cols * RELIEF_SS;
  const h2 = rows * RELIEF_SS;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h2;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const img = ctx.createImageData(w, h2);
  const d = img.data;

  /**
   * Bilinear sample of the coarse height field, in fine-grid coordinates.
   *
   * The base field genuinely is smooth at ten map units, so interpolating it
   * invents nothing — it is the same surface read at a finer step. The *detail*
   * below is what adds structure, and that is a pure function of position which
   * can honestly be evaluated at any pitch.
   */
  const sample = (fx: number, fy: number): number => {
    const x = Math.max(0, Math.min(cols - 1.001, fx));
    const y = Math.max(0, Math.min(rows - 1.001, fy));
    const x0 = x | 0;
    const y0 = y | 0;
    const tx = x - x0;
    const ty = y - y0;
    const k = y0 * cols + x0;
    const a = height[k];
    const b = height[k + 1];
    const c = height[k + cols];
    const e = height[k + cols + 1];
    return (a + (b - a) * tx) * (1 - ty) + (c + (e - c) * tx) * ty;
  };

  /** The shading surface: the real field plus erosion detail where it is rock. */
  const relief = new Float32Array(w * h2);
  /** Whether the cell is an ice province, kept from the first pass for the snow. */
  const icy = new Uint8Array(w * h2);
  const rock = new Uint8Array(w * h2);
  const land = new Uint8Array(w * h2);
  /** Biome per province, looked up once — this loop runs 663,000 times. */
  const kindOf = new Map<number, ProvinceTerrain>();
  for (let py = 0; py < h2; py++) {
    for (let px = 0; px < w; px++) {
      // Through the warp backwards, so the hillshade lies under the coastline
      // and the province rings rather than two map units off them. See
      // `planeSource`.
      const src = planeSource(px / RELIEF_SS, py / RELIEF_SS);
      const fx = src.x;
      const fy = src.y;
      const base = sample(fx, fy);
      const lift = Math.max(0, Math.min(1, base));
      const ox = Math.max(0, Math.min(cols - 1, fx | 0));
      const oy = Math.max(0, Math.min(rows - 1, fy | 0));
      const prov = owner[oy * cols + ox];
      if (prov < 0) {
        relief[py * w + px] = base;
        continue;
      }
      let kind = kindOf.get(prov);
      if (kind === undefined) {
        kind = provinceDef(prov).terrain;
        kindOf.set(prov, kind);
      }
      const isRock = kind === 'mountain' || kind === 'hill';
      land[py * w + px] = 1;
      if (isRock) rock[py * w + px] = 1;
      if (kind === 'ice') icy[py * w + px] = 1;

      /**
       * Ridged multifractal, and the *multi* is what draws the erosion.
       *
       * Folding smooth noise about its midpoint — `1 - |n - 0.5| * 2` — turns
       * its slopes into creases, which is one ridge. Summing folded octaves at
       * doubling frequency and halving weight, each one **damped by the octave
       * above it**, is what makes those ridges branch: a large crease carries
       * finer creases down its own flanks, which is exactly the dendritic spur
       * pattern water cuts into a massif. That damping term is the whole
       * difference between a range and a field of corduroy.
       */
      const oct = isRock ? RELIEF_OCTAVES : 1;
      const amp0 = isRock ? RELIEF_AMP : DETAIL_AMP;
      let sum = 0;
      let weight = 1;
      let amp = 1;
      let freq = isRock ? EROSION_COARSE : 0.34;
      let norm = 0;
      const bend = isRock ? (vnoise2(fx * 0.08, fy * 0.08, 1507) - 0.5) * 2.8 : 0;
      for (let o = 0; o < oct; o++) {
        const nv = 1 - Math.abs(vnoise2((fx + bend) * freq,
          (fy - bend * 0.6) * freq, 907 + o * 13) - 0.5) * 2;
        const v = nv * nv * (isRock ? nv : 1) * weight;
        sum += v * amp;
        norm += amp;
        // The next octave only bites where this one is already high, which is
        // what keeps the detail on the ridges instead of over everything.
        weight = Math.max(0, Math.min(1, v * 1.6));
        amp *= 0.56;
        freq *= EROSION_RATIO;
      }
      const detail = norm > 0 ? sum / norm : 0.5;
      // Hills get the same shape at a third of the depth: a foothill is a
      // dissected mass too, just a shallow one.
      const depth = isRock ? (kind === 'hill' ? 0.42 : 1) : 1;
      relief[py * w + px] =
        base + (detail - 0.5) * amp0 * depth * (isRock ? 1 : lift * lift * 2);
    }
  }

  for (let py = 0; py < h2; py++) {
    for (let px = 0; px < w; px++) {
      const k = py * w + px;
      const o = k * 4;

      /**
       * Water is left alone, and the mask is read at the *raster's* pitch.
       *
       * The sea has its own language here — depth contours drawn from
       * `offshore` — and shading the seabed would fight those rings and imply a
       * bathymetry the simulation does not have. Sampling the mask coarsely
       * rather than interpolating it is deliberate: a soft coastline in this
       * layer would put a half-lit fringe of hillside out over the water.
       */
      const msrc = planeSource(px / RELIEF_SS, py / RELIEF_SS);
      if (!land[k]) {
        d[o + 3] = 0;
        continue;
      }

      // Central differences on the fine grid. The step is now half a cell, so
      // the exaggeration below is per *fine* step and the constants moved with
      // it when this stopped being a one-to-one bake.
      const l = relief[k - (px > 0 ? 1 : 0)];
      const r = relief[k + (px < w - 1 ? 1 : 0)];
      const u = relief[k - (py > 0 ? w : 0)];
      const dn = relief[k + (py < h2 - 1 ? w : 0)];
      const hh = relief[k];
      const lift = Math.max(0, Math.min(1, hh));
      // Gradients are measured per pixel. Compensating for the supersample
      // keeps the same world slope while retaining narrower crest detail.
      const push = (RELIEF_BASE + RELIEF_PEAK * lift * lift) * RELIEF_SS / 2;
      const gx = (r - l) * 0.5 * push;
      const gy = (dn - u) * 0.5 * push;

      /**
       * The surface normal against the light, as a plain dot product.
       *
       * `1 / sqrt(gx² + gy² + 1)` is the normal's z; the x and y components are
       * `-gx` and `-gy` over the same length. The light has no z term because a
       * grazing light is what shows form — a lamp directly overhead lights a
       * mountain and a marsh identically.
       *
       * **Keeping this out of saturation is the whole game.** `lit` tends to
       * ±1 as the gradient grows, and a region that has reached ±1 has no
       * variation left in it: the first cut of this pushed the *median* slope
       * to 2.3 and produced a mask of flat white and flat black, which is
       * precisely why the ranges read as sketch rather than as ground. The
       * constants are chosen so that under a fiftieth of the shaded area is
       * saturated, measured.
       */
      const inv = 1 / Math.sqrt(gx * gx + gy * gy + 1);
      const lighting = (-gx * LIGHT_X - gy * LIGHT_Y) * inv;
      // Small gullies receive an occlusion shadow while narrow crests keep
      // their light. The broad base field stays quiet behind the raised peaks.
      const hollow = rock[k] ? Math.max(0, (l + r + u + dn - hh * 4) * RELIEF_SS * 3.2) : 0;
      const lit = Math.max(-0.85, lighting - Math.min(0.18, hollow));

      /**
       * Snow on the tops, riding the same pixel rather than a fourth layer: an
       * `overlay` of white is already how this layer lightens a slope, so a
       * peak is a cell whose highlight never falls to nothing. Written as a
       * floor under the highlight and a brake on the shadow, so a north face
       * above the snowline stays pale but still has form in it — which a
       * separate additive layer would have flattened.
       */
      /**
       * …and it comes off the steep, which is the half of it a threshold on
       * height alone cannot say.
       *
       * Snow does not lie on a face past about forty degrees; it slides, and
       * what is left is the black rib of rock between two white gullies. That
       * rib is most of what makes a photographed snowfield read as a mountain
       * rather than as a white blanket, and it costs one term: the gradient is
       * already in hand two lines above.
       */
      const steep = Math.min(1, Math.hypot(gx, gy) / 1.7);
      const wy = originY + msrc.y * cell;
      const snow = snowAt(hh, msrc.x, msrc.y, wy, icy[k] === 1) * (1 - steep * 0.62);
      const snowLift = snow * snow * 0.4;

      if (lit >= 0 || snowLift > 0) {
        const a = Math.max(lit, 0) * 0.92 + snowLift;
        d[o] = 231;
        d[o + 1] = 221;
        d[o + 2] = 192;
        d[o + 3] = Math.min(255, a * 255) | 0;
      }
      if (lit < 0) {
        const shade = -lit * (1 - snowLift * 0.7);
        if (shade > 0) {
          /**
           * A snow shadow is blue, and this is the one place the chart may say
           * so. Snow in shade is lit by the sky and nothing else, so its shadow
           * is the sky's colour; ash-black shadow on white ground is the single
           * surest way to make snow read as paint. The ink is mixed toward a
           * cold slate in proportion to how much snow is on the pixel, so a
           * bare crag keeps the near-black the rest of the board uses.
           */
          d[o] = (21 + 55 * snow) | 0;
          d[o + 1] = (28 + 67 * snow) | 0;
          d[o + 2] = (26 + 93 * snow) | 0;
          d[o + 3] = Math.min(255, shade * 255) | 0;
        }
      }
    }
  }

  ctx.putImageData(img, 0, 0);
  try {
    cached = rasterUrl(canvas);
  } catch {
    return '';
  }
  return cached;
}

/** The `<image>` element for the chart, in world units. */
export function reliefLayer(): string {
  const href = reliefImage();
  if (href === '') return '';
  return (
    `<image class="wm-relief" href="${href}" ` +
    `x="${WORLD.originX}" y="${WORLD.originY}" ` +
    `width="${WORLD.w}" height="${WORLD.h}" preserveAspectRatio="none" />`
  );
}


// ── Ground texture ─────────────────────────────────────────────────────────

/**
 * The surface of each country, stamped per cell.
 *
 * The hillshade gives the board its *form*; this gives it its *fabric*. They are
 * different jobs and they are two layers because they blend differently: relief
 * has to lighten and darken, and texture only ever darkens — a canopy, a dune
 * shadow and a tussock are all things that sit on the ground and cast on it.
 *
 * The chart already had a terrain vocabulary — the little triangles and trees
 * from `provinceMarks` — but those are *symbols*, a dozen per province, hung on
 * a flat fill. A symbol says "there is forest here". A texture says how much and
 * how thick, and it says it at every zoom without anybody choosing a density.
 *
 * **The frequencies are set for the fit view, not for a close one.** A raster
 * cell is two map units and the whole 734-unit board lands in about a thousand
 * pixels, so a feature finer than about four cells is sub-pixel at the zoom
 * most players spend most of their time at — it averages to a flat grey and the
 * bake is wasted. The first cut of this was pitched at one to two features per
 * cell and was, correctly, invisible.
 *
 * **Bilinear-smoothed value noise, hashed from cell coordinates.** No image is
 * fetched and nothing is stored: the pattern is a pure function of position, so
 * it is stable across reloads, costs one bake at boot, and cannot drift from the
 * board it is drawn on.
 */

/**
 * Smooth value noise straight in raster-cell space, for the relief detail.
 *
 * Separate from `vn` below, which takes a frequency and is written for the
 * fabric; this one is handed pre-scaled coordinates because the relief mixes
 * three octaves and multiplying the frequency in at each call site reads
 * better than threading it through.
 */
export function vnoise2(x: number, y: number, seed: number): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const tx = x - x0;
  const ty = y - y0;
  const sx = tx * tx * (3 - 2 * tx);
  const sy = ty * ty * (3 - 2 * ty);
  const a = hash2(x0, y0, seed);
  const b = hash2(x0 + 1, y0, seed);
  const c = hash2(x0, y0 + 1, seed);
  const e = hash2(x0 + 1, y0 + 1, seed);
  return (a + (b - a) * sx) * (1 - sy) + (c + (e - c) * sx) * sy;
}

/** Smooth value noise in raster-cell space. `f` is features per cell. */
function vn(x: number, y: number, f: number, seed: number): number {
  const px = x * f;
  const py = y * f;
  const x0 = Math.floor(px);
  const y0 = Math.floor(py);
  const tx = px - x0;
  const ty = py - y0;
  const sx = tx * tx * (3 - 2 * tx);
  const sy = ty * ty * (3 - 2 * ty);
  const a = hash2(x0, y0, seed);
  const b = hash2(x0 + 1, y0, seed);
  const c = hash2(x0, y0 + 1, seed);
  const d = hash2(x0 + 1, y0 + 1, seed);
  return (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
}

/**
 * ── The fabric vocabulary ──────────────────────────────────────────────────
 *
 * Two shared shapers, because every country below is built out of the same two
 * ideas and writing them once is what stops nine biomes drifting into nine
 * different notions of what "a bit of texture" means.
 *
 * **`fbm` is for masses** — anything whose structure is blotchy: canopy,
 * scree, rubble, grain. **`ridged` is for grain that runs** — dunes, fen
 * streaks, crevasses — because folding noise about its midpoint turns its
 * slopes into creases, and a crease is what a lineated surface is made of. It
 * is the same shaper the relief uses for erosion, at a tenth of the depth.
 *
 * **Every frequency below is in raster-cell units, and a cell is two map
 * units.** So `f` of 0.17 is a feature every twelve map units and `f` of 0.9 is
 * one every two. That upper bound is not a preference: the fabric is baked at
 * two pixels to the cell, so anything past about 1.0 is finer than the grid and
 * aliases into noise rather than resolving into detail. That mistake has now
 * been made twice in this file — once in the fabric's first cut, pitched an
 * octave too fine to be visible at all, and once in the relief's, pitched below
 * the sampling rate — so the ceiling is written down here rather than
 * rediscovered a third time.
 */
const FABRIC_FINEST = 1.0;

/** How far the shapers' output is stretched about its own mean. See `fbm`. */
const FABRIC_CONTRAST = 2.1;

/**
 * Fractal Brownian motion: octaves at doubling frequency, halving weight.
 *
 * **The contrast stretch on the way out is not a flourish.** Averaging octaves
 * pulls the result hard toward 0.5 — that is what averaging does — so an fbm
 * built from noise that spanned 0..1 comes out spanning roughly 0.3..0.7. Every
 * threshold below was first written against single-octave noise, and the
 * measured effect of dropping the raw average into them was that **17% of the
 * land got any fabric at all, at a median alpha of 17 in 255**: a texture that
 * was, correctly, invisible. Stretching about the midpoint puts the range back
 * where the thresholds expect it, and does it in one place rather than by
 * re-tuning nine biomes against a moving target.
 */
export function fbm(x: number, y: number, f: number, seed: number, octaves = 3): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let freq = f;
  for (let o = 0; o < octaves; o++) {
    if (freq > FABRIC_FINEST) break;
    sum += vn(x, y, freq, seed + o * 31) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  if (norm <= 0) return 0.5;
  return Math.max(0, Math.min(1, 0.5 + (sum / norm - 0.5) * FABRIC_CONTRAST));
}

/** The same, folded — for surfaces whose grain runs in lines rather than lumps. */
function ridged(x: number, y: number, f: number, seed: number, octaves = 2): number {
  let sum = 0;
  let amp = 1;
  let norm = 0;
  let freq = f;
  for (let o = 0; o < octaves; o++) {
    if (freq > FABRIC_FINEST) break;
    const v = 1 - Math.abs(vn(x, y, freq, seed + o * 37) - 0.5) * 2;
    sum += v * v * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  if (norm <= 0) return 0.5;
  // Folded noise squared already sits low — its mean is about a third — so this
  // is stretched from *its* centre rather than from 0.5, or every ridged
  // surface would come out uniformly dark instead of uniformly creased.
  const mean = 0.33;
  return Math.max(0, Math.min(1, mean + (sum / norm - mean) * FABRIC_CONTRAST));
}

/** A threshold with a soft shoulder, so a texture fades in rather than snapping. */
function above(v: number, cut: number, gain: number): number {
  return v <= cut ? 0 : Math.min(1, (v - cut) / (1 - cut)) * gain;
}

/**
 * How dark this cell's fabric is, 0..1, and what colour that darkness is.
 *
 * Every country gets a *different kind* of mark, not the same speckle at
 * different strengths — that is the whole test: a player should be able to tell
 * forest from fen with the colour taken away.
 *
 * Each is now built from the shapers above rather than from a single noise
 * lookup and a threshold. One octave gives blobs of one size, which is what a
 * cheap texture looks like from any distance; three give a surface that has
 * both a mass and a grain, and reads at the fit view *and* wound in.
 */
function fabricAt(kind: ProvinceTerrain, x: number, y: number): { a: number; warm: boolean } {
  switch (kind) {
    case 'forest': {
      /**
       * Canopy: clumps at the scale of a wood, broken by crown-sized detail,
       * and cut by clearings.
       *
       * The clearing term is what stops a forest being a rectangle of stipple.
       * A wood has holes in it — a burn, a bog, a road — and a second, much
       * coarser noise punching those holes does more for "this is a forest"
       * than any amount of work on the individual crowns.
       */
      const mass = fbm(x, y, 0.17, 17, 3);
      const clearing = vn(x, y, 0.055, 23);
      const open = clearing < 0.36 ? (0.36 - clearing) * 2.4 : 0;
      return { a: Math.max(0, above(mass, 0.4, 1.5) - open), warm: false };
    }
    case 'jungle': {
      // The same, denser and with the clearings nearly closed: a rainforest is
      // a canopy with a roof on it.
      const mass = fbm(x, y, 0.21, 19, 3);
      const clearing = vn(x, y, 0.05, 29);
      const open = clearing < 0.2 ? (0.2 - clearing) * 1.8 : 0;
      return { a: Math.max(0, above(mass, 0.3, 2.0) - open), warm: false };
    }
    case 'desert': {
      /**
       * Dunes: ridged grain combed along one warped axis.
       *
       * The warp is the whole thing. Unwarped, a sine across a plane is
       * corduroy; dragged sideways by a long-wavelength noise it becomes the
       * sinuous, branching crests a sand sea actually has. Two octaves, because
       * a dune field has big dunes with small ones on their flanks.
       */
      const warp = vn(x, y, 0.05, 41) * 13;
      const axis = x * 0.72 + y * 0.28 + warp;
      /**
       * Two dune scales, and the desert is the one country where that is not
       * decoration.
       *
       * A sand sea has long transverse ridges with a finer ripple running over
       * their backs, and the two are at genuinely different sizes — the whole
       * fabric is now the only thing saying "desert", since the dune *symbols*
       * that used to be scattered here have gone. The crest term carries the
       * ridges; the second, four times finer and at a third the weight, is the
       * ripple. Strength is up by half on the same grounds: this is no longer a
       * texture supporting a symbol, it is the statement.
       */
      const crest = ridged(axis, y * 0.35, 0.26, 43, 2);
      const ripple = ridged(axis * 1.4, y * 0.5, 0.62, 47, 1);
      return { a: above(crest, 0.3, 1.5) + above(ripple, 0.55, 0.45), warm: true };
    }
    case 'marsh': {
      /**
       * Fen: standing water lies in the contour, so the grain runs flat and
       * braids. Sampled with y compressed four to one, which is what turns
       * isotropic noise into streaks without a second function.
       */
      const streak = ridged(x * 0.55, y * 2.2, 0.34, 53, 2);
      const pools = vn(x, y, 0.12, 59);
      return { a: above(streak, 0.42, 1.35) * (0.55 + pools * 0.75), warm: false };
    }
    case 'mountain':
    case 'hill': {
      /**
       * Scree, and deliberately quiet now.
       *
       * The relief layer took over the *form* of high ground when the peak
       * symbols were removed, and a strong fabric on top of shaded relief fights
       * it — the shading says where the slope faces and the speckle says
       * nothing, so the speckle only muddies it. What is left is a broken
       * mineral grain fine enough to read as rock and weak enough to stay out
       * of the way.
       */
      const rock = fbm(x, y, 0.42, 67, 3);
      return { a: above(rock, 0.5, 0.75) * (kind === 'hill' ? 0.6 : 1), warm: false };
    }
    case 'ice': {
      // Crevasses: the one fabric that *lightens*, because on ice the crack is
      // the shadow and the field is the bright part. Ridged, so they run.
      const crack = ridged(x, y, 0.16, 71, 2);
      return { a: above(crack, 0.72, 1.5), warm: false };
    }
    case 'ruin': {
      // Rubble, and the coarse term is old field boundaries under it — the
      // reason a ruin province is not simply a rocky one.
      const heap = fbm(x, y, 0.5, 83, 2);
      const plots = ridged(x * 0.8, y * 0.8, 0.11, 89, 1);
      return { a: above(heap, 0.52, 1.1) + above(plots, 0.85, 0.5), warm: true };
    }
    case 'coast': {
      // Shore: ripple parallel to nothing in particular, fine and low. A beach
      // is the smoothest ground on the board and should read that way.
      const ripple = ridged(x * 1.3, y * 0.9, 0.4, 101, 2);
      return { a: above(ripple, 0.55, 0.55), warm: true };
    }
    default: {
      /**
       * Open country: a grain, and a *field pattern* under it.
       *
       * The plain is over a seventh of the board and was the one country with
       * nothing but even speckle — which at the fit view is indistinguishable
       * from no texture at all. The coarse ridged term gives it the faint
       * lineation of worked ground, which is both what a plain looks like from
       * the air and what says somebody lives there.
       */
      const grain = fbm(x, y, 0.34, 97, 2);
      const fields = ridged(x * 0.9, y * 1.1, 0.09, 103, 1);
      return { a: above(grain, 0.55, 0.6) + above(fields, 0.8, 0.42), warm: true };
    }
  }
}

let texCached = '';

/**
 * How many texture pixels to a raster cell.
 *
 * The relief is left at the raster's own pitch on purpose — the height field is
 * smooth at ten map units, so a sharper bake would invent detail it does not
 * have, and soft is what a hillshade should look like anyway. The fabric is the
 * opposite case: it is a *pure function of position*, so it can be evaluated at
 * any pitch for nothing but pixels, and its edges are the whole read. Baked
 * one-to-one it goes visibly soft two notches into the zoom, which is where a
 * player looks at ground.
 *
 * Two, and not four: this is a data URL held in the document, and the bake is
 * quadratic in this number.
 */
const TEX_SS = 2;

/**
 * The hardest this layer may ever darken the ground, 0..1.
 *
 * **On a multiply layer the colour *is* the multiplier**, and the first cut of
 * this forgot it twice over: the inks were near-black (22,30,18 for anything
 * green) and the alpha ramps ran to 0.91 unclamped. Multiplying the ground by
 * roughly 0.17 is not a texture, it is a hole — the owner reported "random
 * black shadow spots on the map" and they were mine, worst over rock and ruin
 * where the ramps peaked hardest.
 *
 * Two changes, and both were needed. The inks are lifted well off black, so
 * that a cell at full strength multiplies by about 0.8 rather than by 0.17; and
 * every ramp is capped here rather than at its own call site, so a new country
 * added below cannot reintroduce the fault by being enthusiastic about its own
 * numbers. Fabric is a tint on ground, never a stain in it.
 */
const FABRIC_MAX = 0.46;

/** The ground fabric as a cached image URL, computed on first ask and kept. */
export function textureImage(): string {
  if (texCached !== '') return texCached;
  const { cols, rows, owner } = RASTER;
  const w = cols * TEX_SS;
  const h = rows * TEX_SS;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(w, h);
  const d = img.data;

  /**
   * The biome per province, looked up once.
   *
   * `provinceDef` is a table read, but it is a table read per cell over ninety
   * thousand cells and the answer only changes per province.
   */
  const kindOf = new Map<number, ProvinceTerrain>();

  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      // Through the warp backwards, like the hillshade above and the strand in
      // `shore.ts`: a fen's reeds have to grow inside the fen that is drawn,
      // not inside the raster cell it was traced from. The mask is still read
      // at the raster's own pitch — nearest, never interpolated — or the fabric
      // would spill a pixel into the sea along every shore.
      const src = planeSource(px / TEX_SS, py / TEX_SS);
      const col = Math.max(0, Math.min(cols - 1, src.x | 0));
      const row = Math.max(0, Math.min(rows - 1, src.y | 0));
      const prov = owner[row * cols + col];
      const o = (py * w + px) * 4;
      if (prov < 0) {
        d[o + 3] = 0;
        continue;
      }
      let kind = kindOf.get(prov);
      if (kind === undefined) {
        kind = provinceDef(prov).terrain;
        kindOf.set(prov, kind);
      }
      // Sampled in *cell* space, so the feature scale is unchanged by the
      // supersample — only the edges get sharper.
      const f = fabricAt(kind, px / TEX_SS, py / TEX_SS);
      if (f.a <= 0) {
        d[o + 3] = 0;
        continue;
      }
      if (kind === 'ice') {
        d[o] = 226;
        d[o + 1] = 238;
        d[o + 2] = 246;
      } else if (f.warm) {
        d[o] = 122;
        d[o + 1] = 100;
        d[o + 2] = 68;
      } else {
        d[o] = 70;
        d[o + 1] = 86;
        d[o + 2] = 60;
      }
      d[o + 3] = Math.min(255, f.a * FABRIC_MAX * 255) | 0;
    }
  }

  ctx.putImageData(img, 0, 0);
  try {
    texCached = rasterUrl(canvas);
  } catch {
    return '';
  }
  return texCached;
}

/** The `<image>` element for the fabric, in world units. */
export function textureLayer(): string {
  const href = textureImage();
  if (href === '') return '';
  return (
    `<image class="wm-fabric" href="${href}" ` +
    `x="${WORLD.originX}" y="${WORLD.originY}" ` +
    `width="${WORLD.w}" height="${WORLD.h}" preserveAspectRatio="none" />`
  );
}


// ── The water ──────────────────────────────────────────────────────────────

/**
 * Shallows: the sea shaded by how far it is from land.
 *
 * `RealmRaster.offshore` is map units from each water cell to the nearest
 * shore. It has been computed since the raster existed — the sim uses it to
 * decide what is coastal and where a lane may run — and the chart drew the
 * whole ocean as one radial gradient, so a strait and the deep middle of the
 * Weltering were the same colour.
 *
 * A shelf is the single most legible thing on any real sea chart: it says where
 * a coast *is* before the coastline itself registers, it makes an island read as
 * an island rather than as a hole in the water, and on this board it says which
 * crossings are narrow. The same argument as the hillshade, and the same
 * answer — the field was already there.
 *
 * A narrow shelf gives the coastline depth. Beyond it the sea is dark slate
 * with fine material grain. Engraved currents are the chart's own shared SVG
 * pattern, so zooming keeps the lines crisp instead of enlarging raster bands.
 */

/** Where the shelf ends, in map units of distance from land. */
const SHELF = 3.2;

/**
 * …and where the water stops getting any deeper, for drawing purposes.
 *
 * The shelf answered a question about the *coast*. It left the rest of the
 * ocean — and measured, that is 61.5% of the water on this board — as one flat
 * tone, with the only variation coming from a radial gradient centred on the
 * sheet. That is a statement about the paper again: a bay at the middle of the
 * page came out brighter than a bay at its corner because of where it happened
 * to be printed, not because of anything about the water.
 *
 * `offshore` runs to 225 map units with a median of 36 and a p90 of 129, so a
 * ramp out to 150 covers all but the abyssal middle of the two great oceans and
 * puts a real difference between a strait, a gulf and open water. Four broad
 * steps, at a fifth of the shelf's strength: this is the slope beyond the shelf
 * and it may not compete with it.
 */
const DEEP = 110;
const SEA_SS = 3;

let seaCached = '';

export function seaImage(): string {
  if (seaCached !== '') return seaCached;
  const { cols, rows, owner, offshore } = RASTER;
  const w = cols * SEA_SS, h = rows * SEA_SS;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const img = ctx.createImageData(w, h);
  const d = img.data;

  for (let py = 0; py < h; py++) {
    for (let px = 0; px < w; px++) {
      const src = planeSource(px / SEA_SS, py / SEA_SS);
      const sx = Math.max(0, Math.min(cols - 1.001, src.x));
      const sy = Math.max(0, Math.min(rows - 1.001, src.y));
      const ix = sx | 0, iy = sy | 0;
      const k = iy * cols + ix;
      const o = (py * w + px) * 4;
      // The land stays transparent; shore.ts owns beaches and cliff material.
      if (owner[k] >= 0) continue;
      const tx = sx - ix, ty = sy - iy;
      const dist = (offshore[k] * (1 - tx) + offshore[k + 1] * tx) * (1 - ty) +
        (offshore[k + cols] * (1 - tx) + offshore[k + cols + 1] * tx) * ty;
      const shelf = Math.pow(Math.max(0, 1 - dist / SHELF), 2.5);
      const gulf = Math.max(0, 1 - dist / DEEP);
      const grain = (hash2(px, py, 1273) - 0.5) * 1.8;
      const swell = (vnoise2(src.x * 0.038, src.y * 0.07, 1277) - 0.5) * 1.2;
      d[o] = 25 + gulf * 3 + shelf * 24 + grain + swell;
      d[o + 1] = 34 + gulf * 4 + shelf * 25 + grain + swell;
      d[o + 2] = 38 + gulf * 4 + shelf * 21 + grain + swell;
      d[o + 3] = 255;
    }
  }

  ctx.putImageData(img, 0, 0);
  try {
    seaCached = rasterUrl(canvas);
  } catch {
    return '';
  }
  return seaCached;
}

/** The `<image>` element for the shelf, in world units. */
export function seaLayer(): string {
  const href = seaImage();
  if (href === '') return '';
  return (
    `<image class="wm-shelf" href="${href}" ` +
    `x="${WORLD.originX}" y="${WORLD.originY}" ` +
    `width="${WORLD.w}" height="${WORLD.h}" preserveAspectRatio="none" />`
  );
}

// ── Standing water, off the river pass's own depression fill ─────────────

let lakeCache: Float32Array | null = null;

let filledCache: Float32Array | null = null;

/**
 * **The depressions are filled first, and skipping this was the whole bug.**
 *
 * A noise-built continent is full of local minima — measured on this board,
 * 105 of them. Routed on the raw field, water reaching one has nowhere to go
 * and the course stops: **only 25.5% of river cells actually reached the sea,
 * and 1,312 of 1,761 ended in a pit in the middle of the land.** That is
 * precisely what "the rivers do not make sense" looks like from the outside —
 * streams that start in the hills, run a little way, and simply stop.
 *
 * The first cut argued the pits away as tarns. That was wrong twice: three
 * quarters is not an occasional tarn, it is the network failing, and a real
 * tarn has an outlet the moment it fills.
 *
 * So: priority-flood, which is the standard preprocessing for exactly this.
 * Every cell touching the sea goes into a queue keyed by height; the lowest is
 * popped and its neighbours are raised to at least its own level before being
 * pushed in turn. Because cells always leave the queue in ascending order,
 * each is assigned the lowest level at which water could have reached it —
 * which is its spill point. Afterwards **every land cell has a downhill path
 * to the sea by construction**, and a filled basin drains over its own rim
 * exactly where a real lake would overflow.
 *
 * The filled field is used for routing only. `RASTER.height` is untouched, so
 * the hillshade still draws the real ground with its real hollows in it.
 */
export function filledHeights(): Float32Array {
  if (filledCache) return filledCache;
  const { cols, rows, owner, height } = RASTER;
  const n = cols * rows;
  const filled = new Float32Array(n);
  const seen = new Uint8Array(n);
  // A binary heap of cells keyed by their fill level. An array of two
  // parallel stacks rather than objects: this runs over sixty thousand cells
  // and allocating a wrapper for each is the only part of this that would be
  // slow.
  const hv: number[] = [];
  const hk: number[] = [];
  const push = (level: number, cellIdx: number): void => {
    hv.push(level);
    hk.push(cellIdx);
    let i = hv.length - 1;
    while (i > 0) {
      const par = (i - 1) >> 1;
      if (hv[par] <= hv[i]) break;
      [hv[par], hv[i]] = [hv[i], hv[par]];
      [hk[par], hk[i]] = [hk[i], hk[par]];
      i = par;
    }
  };
  const pop = (): number => {
    const top = hk[0];
    const lv = hv.pop() as number;
    const lk = hk.pop() as number;
    if (hv.length > 0) {
      hv[0] = lv;
      hk[0] = lk;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = l + 1;
        let m = i;
        if (l < hv.length && hv[l] < hv[m]) m = l;
        if (r < hv.length && hv[r] < hv[m]) m = r;
        if (m === i) break;
        [hv[m], hv[i]] = [hv[i], hv[m]];
        [hk[m], hk[i]] = [hk[i], hk[m]];
        i = m;
      }
    }
    return top;
  };

  // Seed with every land cell that touches water — the rim the flood starts
  // from. A landlocked island of the raster with no such cell simply keeps
  // its own heights, which is correct: it has no outlet.
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const k = row * cols + col;
      if (owner[k] < 0) continue;
      let coastal = false;
      for (let dy = -1; dy <= 1 && !coastal; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const cx = col + dx;
          const cy = row + dy;
          if (cx < 0 || cy < 0 || cx >= cols || cy >= rows) continue;
          if (owner[cy * cols + cx] < 0) {
            coastal = true;
            break;
          }
        }
      }
      if (coastal) {
        filled[k] = height[k];
        seen[k] = 1;
        push(height[k], k);
      }
    }
  }

  while (hv.length > 0) {
    const k = pop();
    const col = k % cols;
    const row = (k / cols) | 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (dx === 0 && dy === 0) continue;
        const cx = col + dx;
        const cy = row + dy;
        if (cx < 0 || cy < 0 || cx >= cols || cy >= rows) continue;
        const j = cy * cols + cx;
        if (owner[j] < 0 || seen[j]) continue;
        seen[j] = 1;
        /**
         * The epsilon is what keeps a filled basin *drainable*.
         *
         * Filled dead level, a basin is a plateau of identical heights and D8
         * has no downhill neighbour to pick anywhere inside it — the pit comes
         * straight back, one cell wider. A hair of gradient toward the outlet
         * gives every cell in the basin somewhere to go while staying far
         * below anything the eye or the biome chooser could notice.
         */
        filled[j] = Math.max(height[j], filled[k] + 1e-5);
        push(filled[j], j);
      }
    }
  }
  // Anything the flood never reached — a raster island with no coast — keeps
  // its own ground rather than a zero.
  for (let k = 0; k < n; k++) if (!seen[k]) filled[k] = height[k];
  filledCache = filled;
  return filled;
}

/**
 * How deep the standing water is on each land cell, in the height field's units.
 *
 * **This is the one field the chart already owned and never drew.** The river
 * routing has to fill every depression before it can trace a course — that is
 * the pass that took rivers reaching the sea from 25.5% to 100% — and the amount
 * it had to raise a cell by *is* the water a basin holds. `filled − height` is a
 * lake, exactly, with its surface at the spill point and its outlet where the
 * river leaves. Nothing here is invented, nothing is placed, and a mere cannot
 * disagree with the stream draining it because they are the same computation.
 *
 * Zero on nearly all of the board, which is right: a lake is a rare thing and a
 * world speckled with ponds would be a world with a noise function in it.
 */
export function lakeDepth(): Float32Array {
  if (lakeCache) return lakeCache;
  const { cols, rows, owner, height } = RASTER;
  const filled = filledHeights();
  const n = cols * rows;
  const out = new Float32Array(n);
  for (let k = 0; k < n; k++) {
    if (owner[k] < 0) continue;
    const d = filled[k] - height[k];
    // The epsilon the fill adds to keep a basin drainable is 1e-5 a cell and
    // accumulates along a basin's length. Anything under a thousandth is that
    // bookkeeping and not water.
    out[k] = d > 1e-3 ? d : 0;
  }
  lakeCache = out;
  return out;
}
